import { CachedRecentSearch } from '@/redux/api/saveSearch';
import { GlobalState } from '@/redux/store';
import { SearchParams } from '@/types/SaveSearch';
import { getKeywordFromQueryParams, parseQueryParams } from '@/utils/queryParams';

export type SaveSearchParamsOrKey = SearchParams | string;

const normalizeRecentSearchKeyword = (keyword: string) => keyword.trim().toLowerCase();

export const getRecentSearchKeywordFromParams = (saveSearchParams: SaveSearchParamsOrKey): string => {
    if (typeof saveSearchParams === 'string') {
        return saveSearchParams.trim();
    }

    const keyword = saveSearchParams.keyword;

    return typeof keyword === 'string' ? keyword.trim() : String(keyword ?? '').trim();
};

/**
 * Stays tolerant of a malformed entry on purpose. This feeds the header, which renders on every
 * page, so a throw here takes the whole app down through the root ErrorBoundary. A bad entry has
 * to degrade to a missing chip instead.
 */
export const getRecentSearchQuery = (recentSearch: CachedRecentSearch | null | undefined): string => {
    if (!recentSearch) {
        return '';
    }

    const keywordFromFacets = getKeywordFromQueryParams(recentSearch.facets);

    if (keywordFromFacets) {
        return String(keywordFromFacets);
    }

    const url = recentSearch.url ?? '';
    const queryString = url.includes('?') ? url.split('?')[1] : '';
    const keywordFromUrl = getKeywordFromQueryParams(parseQueryParams(queryString));

    return keywordFromUrl ? String(keywordFromUrl) : '';
};

export const isKeywordInRecentSearches = (state: GlobalState, keyword: string): boolean => {
    const normalizedKeyword = normalizeRecentSearchKeyword(keyword);

    if (!normalizedKeyword) {
        return false;
    }

    return state.saveSearch.recentSearches.some((recentSearch) => {
        const query = getRecentSearchQuery(recentSearch);

        return normalizeRecentSearchKeyword(query) === normalizedKeyword;
    });
};
